import { useState, useEffect, useRef } from 'react';
import { confirm } from '../utils/confirm';
import { fmtDate, fmtDateTime } from '../utils/format';
import {
  getDebtRecovery, saveDebtRecovery, scanRecoveryRevenue, getRecoveryScanStatus,
  closeDebtRecovery, getDomesticFees,
} from '../api/debts';

// ---- Định dạng số/tiền (mono) ----
const num = (x) => Math.round(Number(x) || 0).toLocaleString('vi-VN');
const money = (x) => num(x) + 'đ';
const signMoney = (x) => (x < 0 ? '-' : '') + money(Math.abs(x));
const pctStr = (r) => (r < 0 ? '-' : '') + Math.abs(r).toFixed(2).replace('.', ',') + '%'; // r tính theo %

// ---- Tông màu (theo handoff) ----
const rateTone = (r) => // r theo %
  r >= 70 ? { c: '#0f7a55', bg: 'var(--bct-accent-soft)', fill: '#12a26e' }
  : r >= 40 ? { c: '#a06a1a', bg: 'var(--bct-amber-soft)', fill: '#e0a400' }
  : { c: '#c0392b', bg: 'var(--bct-red-soft)', fill: '#d64534' };
const dayTone = (d) =>
  d >= 30 ? { c: '#c0392b', bg: 'var(--bct-red-soft)' }
  : d >= 20 ? { c: '#a06a1a', bg: 'var(--bct-amber-soft)' }
  : { c: '#b07d00', bg: '#fbf3e0' };

const SALE_MIN = 20e6;   // ẩn sale nợ < 20tr trong thẻ chi nhánh
const COD_MIN_DAYS = 10; // COD: chỉ hiện KH nợ > 10 ngày
const COD_SHOW = 8;      // số dòng COD hiển thị tối đa

// CSS token cục bộ (không rò ra ngoài) + hỗ trợ dark theme.
const SCOPE_CSS = `
.bct {
  --bct-surface:#ffffff; --bct-band:#f0f1f4; --bct-hover:#fafbfc; --bct-chip:#f2f3f6;
  --bct-border:#e4e7ea; --bct-line:#eef0f2;
  --bct-text:#1a1d23; --bct-text3:#565b64; --bct-muted:#6b7078; --bct-muted2:#8a8f97;
  --bct-accent:#1f9d57; --bct-accent-d:#178a4a; --bct-accent-soft:#e7f6ed;
  --bct-red:#d64534; --bct-red-soft:#fdeceb; --bct-amber:#b07d00; --bct-amber-soft:#fbf1da;
  font-family:'IBM Plex Sans',system-ui,sans-serif;
}
:root[data-theme="dark"] .bct, .bct[data-theme="dark"] {
  --bct-surface:#1c1f27; --bct-band:#232733; --bct-hover:#232733; --bct-chip:#2a2f3a;
  --bct-border:#2f333d; --bct-line:#282d37;
  --bct-text:#e7e9ed; --bct-text3:#aab0ba; --bct-muted:#98a0ab; --bct-muted2:#828b96;
  --bct-accent-soft:#123324; --bct-red-soft:#3a201d; --bct-amber-soft:#332a15;
}
.bct .mono { font-family:'IBM Plex Mono',ui-monospace,monospace; font-variant-numeric:tabular-nums; }
.bct .bct-row:hover { background:var(--bct-hover); }
.bct .bct-week:hover { border-color:var(--bct-accent); }
.bct-linkbtn { background:none; border:none; padding:0; cursor:pointer; color:var(--bct-accent); font:inherit; font-weight:600; }
`;

export default function WeeklyDebtReport({ detail, groupData, reports, canScan, canClose, scanning, sending, onScanDebt, onSend, onOpenCustomer, onOpenReport, toast }) {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 86400000);
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const [data, setData] = useState(null);       // getDebtRecovery
  const [rows, setRows] = useState([]);         // { group, dauky, doanhthu, conlai }
  const [ship, setShip] = useState(null);       // getDomesticFees
  const [from, setFrom] = useState(ymd(weekAgo));
  const [to, setTo] = useState(ymd(today));
  const [rScan, setRScan] = useState(false);    // đang quét DT tuần (recovery)
  const [busy, setBusy] = useState(false);
  const dirty = useRef(false);

  const loadRecovery = () => getDebtRecovery().then((d) => {
    setData(d);
    if (!dirty.current) setRows((d.rows || []).map((r) => ({ group: r.group, dauky: r.dauky, doanhthu: r.doanhthu, conlai: r.conlai })));
  });
  useEffect(() => { loadRecovery().catch(() => {}); getDomesticFees().then(setShip).catch(() => {}); }, []);

  const closed = !!data?.closed;
  const comp = (r) => { const ps = (r.dauky || 0) + (r.doanhthu || 0); const dathu = ps - (r.conlai || 0); return { ps, dathu, rate: ps > 0 ? (dathu / ps) * 100 : 0 }; };

  // Ghép recovery + danh sách sale (theo nhóm). GTS đứng đầu, rồi theo Còn lại giảm dần.
  const gmap = {}; (groupData?.groups || []).forEach((g) => { gmap[g.group] = g; });
  let cards = rows.map((r) => ({ r, g: gmap[r.group] || { sales: [], orders: 0 } }));
  (groupData?.groups || []).forEach((g) => { if (!rows.find((r) => r.group === g.group)) cards.push({ r: { group: g.group, dauky: 0, doanhthu: 0, conlai: g.total }, g }); });
  cards = cards.filter((c) => c.r.group !== 'Chưa thuộc nhóm');
  cards.sort((a, b) => (a.r.group === 'GTS' ? -1 : b.r.group === 'GTS' ? 1 : (b.r.conlai || 0) - (a.r.conlai || 0)));

  // Tổng quan (summary strip).
  const tot = rows.reduce((a, r) => { const c = comp(r); a.ps += c.ps; a.dathu += c.dathu; a.conlai += r.conlai || 0; return a; }, { ps: 0, dathu: 0, conlai: 0 });
  const totRate = tot.ps > 0 ? (tot.dathu / tot.ps) * 100 : 0;

  // COD chưa thu: ưu tiên nợ lâu/nợ lớn.
  const codAll = (detail?.items || []).filter((r) => (r.maxDebtDays || 0) > COD_MIN_DAYS).sort((a, b) => b.debt - a.debt);
  const codRows = codAll.slice(0, COD_SHOW);
  const codMore = codAll.length - codRows.length;

  // Cước nội địa & NVL.
  const shipCust = ship?.customers || [];
  const shipOver = shipCust.filter((c) => (c.debtDays ?? c.maxDebtDays ?? 0) > COD_MIN_DAYS).length;
  const shipTop = shipCust.slice(0, 3);

  async function doScan() {
    setRScan(true);
    try {
      await scanRecoveryRevenue(from, to);
      toast('Đã bắt đầu quét doanh thu tuần (3 nguồn) ở nền…');
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await getRecoveryScanStatus().catch(() => null);
        if (s && s.scan?.status !== 'running') { s.scan?.status === 'error' ? toast(`Lỗi quét: ${s.scan.error || '?'}`, 'danger') : toast('Đã quét xong doanh thu tuần.'); dirty.current = false; await loadRecovery(); break; }
      }
    } catch (e) { toast(e.response?.data?.message || 'Lỗi khi quét doanh thu', 'danger'); }
    finally { setRScan(false); }
  }
  function pullAuto() {
    const s = data?.suggest || {};
    setRows((rs) => rs.map((r) => ({ ...r, dauky: s.dauky?.[r.group] ?? r.dauky, conlai: s.conlai?.[r.group] ?? r.conlai })));
    dirty.current = true;
    toast('Đã lấy lại đầu kỳ (tuần trước) + còn lại (từ quét công nợ). Nhớ Chốt tuần để lưu.');
  }
  async function closeWeek() {
    if (!await confirm('Chốt bảng thu hồi công nợ tuần này? "Còn lại" sẽ thành "đầu kỳ" của tuần sau.')) return;
    setBusy(true);
    try { await saveDebtRecovery(rows); const r = await closeDebtRecovery(); dirty.current = false; toast(`Đã chốt ${r.label}`); await loadRecovery(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi khi chốt', 'danger'); }
    finally { setBusy(false); }
  }

  const scan = data?.scan || {};
  const wk = data?.week || {};
  const scroller = useRef(null);

  // Dải tuần: các BÁO CÁO đã nộp + thẻ tuần hiện tại (active, ngoài cùng phải).
  // Thẻ hiện tại dùng TỔNG COD QUÉT TRỰC TIẾP (detail.total) — cùng nguồn với tab
  // "Công nợ chi tiết" và cùng bản chất với totalDebt của các thẻ báo cáo, để các
  // con số trên màn khớp nhau (KHÔNG dùng "còn lại" bảng thu hồi vì nó gồm cả
  // cước/NVL và bị đông cứng khi chốt tuần).
  const weekCards = [
    ...(reports || []).map((r) => ({ id: r._id, label: r.label, debt: r.totalDebt, overdue: r.longCount ?? r.longCount, active: false })),
    { id: null, label: wk.label || 'Tuần này', debt: detail?.total, overdue: codAll.length, active: true },
  ];

  return (
    <div className="bct">
      <style>{SCOPE_CSS}</style>

      {/* Sub-header: nhãn tuần + hành động */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '2px 2px 14px' }}>
        <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
          <span style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--bct-accent-soft)', color: 'var(--bct-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', fontSize: 19 }}>📊</span>
          <div>
            <div style={{ fontSize: 21, fontWeight: 700, color: 'var(--bct-text)', letterSpacing: '-0.01em' }}>Báo cáo tuần · Công nợ</div>
            <div className="mono" style={{ fontSize: 12.5, color: 'var(--bct-muted)', marginTop: 2 }}>{wk.label || '—'}{closed ? ' · 🔒 đã chốt' : ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {detail?.syncedAt && <span className="mono" style={{ fontSize: 11.5, color: 'var(--bct-muted2)' }}>Quét {fmtDateTime(detail.syncedAt)}{detail.from ? ` · ${fmtDate(detail.from)} → ${fmtDate(detail.to)}` : ''}</span>}
          {canScan && <button className="btn sm ghost" disabled={scanning} onClick={onScanDebt}>{scanning ? 'Đang quét…' : '↻ Quét công nợ'}</button>}
          <button className="btn sm" style={{ background: 'var(--bct-accent)', borderColor: 'var(--bct-accent)' }} disabled={sending} onClick={onSend}>{sending ? 'Đang chốt…' : 'Gửi báo cáo (chốt tuần)'}</button>
        </div>
      </div>

      {/* Status bar */}
      {scan.at && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bct-accent-soft)', borderRadius: 10, padding: '9px 14px', marginBottom: 14 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: scan.status === 'error' ? 'var(--bct-red)' : 'var(--bct-accent)' }} />
          <span style={{ fontSize: 12.5, color: 'var(--bct-accent-d)' }}>{scan.byName || 'Tự động'} — {scan.status === 'error' ? `lỗi: ${scan.error}` : 'quét công nợ thành công'} lúc {fmtDateTime(scan.at)}.</span>
        </div>
      )}

      {/* Dải tuần (timeline ngang) */}
      {weekCards.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0 14px', borderBottom: '1px solid var(--bct-line)', marginBottom: 16 }}>
          <button className="btn sm ghost" style={{ width: 30, height: 30, padding: 0, flex: '0 0 auto' }} onClick={() => scroller.current?.scrollBy({ left: -300, behavior: 'smooth' })}>‹</button>
          <div ref={scroller} style={{ display: 'flex', gap: 10, overflowX: 'auto', flex: 1 }}>
            {weekCards.map((w, i) => (
              <div key={i} className="bct-week" onClick={() => !w.active && w.id && onOpenReport?.(w)} style={{ minWidth: 174, flex: '0 0 auto', borderRadius: 11, padding: '9px 13px', cursor: w.active ? 'default' : 'pointer', border: `${w.active ? 1.5 : 1}px solid ${w.active ? 'var(--bct-accent)' : 'var(--bct-border)'}`, background: w.active ? 'var(--bct-accent-soft)' : 'var(--bct-surface)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: w.active ? 'var(--bct-accent-d)' : 'var(--bct-text)' }}>{w.label}</span>
                  {w.active && <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--bct-accent-d)', background: 'var(--bct-surface)', border: '1px solid var(--bct-accent)', borderRadius: 5, padding: '1px 5px', whiteSpace: 'nowrap' }}>TRỰC TIẾP</span>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}><span style={{ color: 'var(--bct-muted)' }}>Tổng nợ</span><span className="mono" style={{ fontWeight: 700, color: 'var(--bct-red)' }}>{money(w.debt)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginTop: 2 }}><span style={{ color: 'var(--bct-muted)' }}>KH nợ lâu</span><span className="mono" style={{ fontWeight: 700, color: 'var(--bct-accent-d)' }}>{w.overdue ?? '—'}</span></div>
              </div>
            ))}
          </div>
          <button className="btn sm ghost" style={{ width: 30, height: 30, padding: 0, flex: '0 0 auto' }} onClick={() => scroller.current?.scrollBy({ left: 300, behavior: 'smooth' })}>›</button>
        </div>
      )}

      {/* Toolbar DT tuần */}
      {!closed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', padding: '2px 0 16px', borderBottom: '1px solid var(--bct-line)', marginBottom: 18 }}>
          <span style={{ fontSize: 12.5, color: 'var(--bct-muted)', fontWeight: 500 }}>DT tuần</span>
          <input type="date" className="mono" style={{ width: 'auto' }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <span style={{ color: 'var(--bct-muted)' }}>→</span>
          <input type="date" className="mono" style={{ width: 'auto' }} value={to} onChange={(e) => setTo(e.target.value)} />
          <button className="btn sm ghost" disabled={rScan} onClick={doScan}>{rScan ? 'Đang quét…' : '↻ Quét DT tuần'}</button>
          <button className="btn sm ghost" disabled={busy} onClick={pullAuto} title="Đầu kỳ (tuần trước) + Còn lại (từ quét công nợ)">↻ Lấy lại tự động</button>
          <div style={{ flex: 1 }} />
          {canClose && <button className="btn sm" style={{ background: 'var(--bct-accent)', borderColor: 'var(--bct-accent)' }} disabled={busy} onClick={closeWeek}>Chốt tuần</button>}
        </div>
      )}

      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr 1fr 1.1fr', border: '1px solid var(--bct-border)', borderRadius: 14, overflow: 'hidden', marginBottom: 22 }}>
        {[
          { lb: 'Tổng công nợ toàn công ty', v: money(tot.conlai), sz: 25, c: 'var(--bct-red)' },
          { lb: 'Đã thu tuần', v: signMoney(tot.dathu), sz: 17, c: tot.dathu < 0 ? 'var(--bct-red)' : 'var(--bct-accent-d)' },
          { lb: 'Phát sinh tuần', v: money(tot.ps), sz: 17, c: 'var(--bct-text)' },
          { lb: 'Tỷ lệ thu hồi chung', v: pctStr(totRate), sz: 19, c: 'var(--bct-amber)', bar: totRate },
        ].map((x, i) => (
          <div key={i} style={{ padding: '16px 18px', borderLeft: i ? '1px solid var(--bct-border)' : 'none' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--bct-muted)', textTransform: 'uppercase', letterSpacing: '.03em' }}>{x.lb}</div>
            <div className="mono" style={{ fontSize: x.sz, fontWeight: 700, color: x.c, marginTop: 6 }}>{x.v}</div>
            {x.bar != null && <div style={{ height: 6, background: 'var(--bct-band)', borderRadius: 4, marginTop: 8, overflow: 'hidden' }}><div style={{ width: `${Math.max(0, Math.min(100, x.bar))}%`, height: '100%', background: 'var(--bct-amber)' }} /></div>}
          </div>
        ))}
      </div>

      {/* Chi nhánh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ width: 4, height: 15, borderRadius: 2, background: 'var(--bct-accent)' }} />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--bct-text)' }}>Công nợ &amp; tỷ lệ thu hồi theo chi nhánh</span>
        <span style={{ fontSize: 12, color: 'var(--bct-muted2)' }}>— xếp theo thứ hạng thu hồi</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 26 }}>
        {cards.map((c, idx) => {
          const cm = comp(c.r);
          const t = rateTone(cm.rate);
          const sales = (c.g.sales || []).filter((s) => s.debt >= SALE_MIN);
          const hidden = (c.g.sales || []).length - sales.length;
          return (
            <div key={c.r.group} style={{ border: '1px solid var(--bct-border)', borderRadius: 13, background: 'var(--bct-surface)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--bct-line)' }}>
                <span className="mono" style={{ width: 22, height: 22, borderRadius: 7, background: 'var(--bct-band)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--bct-text3)' }}>{idx + 1}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--bct-text)', flex: 1 }}>{c.r.group}</span>
                <span style={{ fontSize: 11, color: 'var(--bct-muted2)' }}>{c.g.orders || 0} đơn</span>
              </div>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--bct-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Còn lại</span>
                  <span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: t.c, background: t.bg, padding: '2px 8px', borderRadius: 6 }}>{pctStr(cm.rate)}</span>
                </div>
                <div className="mono" style={{ fontSize: 19, fontWeight: 700, color: 'var(--bct-red)', margin: '3px 0 8px' }}>{money(c.r.conlai)}</div>
                <div style={{ height: 6, background: 'var(--bct-band)', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: `${Math.max(2, Math.min(100, cm.rate))}%`, height: '100%', background: t.fill }} /></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 9, fontSize: 12 }}>
                  <span style={{ color: 'var(--bct-muted)' }}>Đã thu tuần</span>
                  <span className="mono" style={{ fontWeight: 700, color: cm.dathu < 0 ? 'var(--bct-red)' : 'var(--bct-accent-d)' }}>{signMoney(cm.dathu)}</span>
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--bct-line)', padding: '8px 14px 10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 26px 84px', fontSize: 10, fontWeight: 600, color: 'var(--bct-muted2)', textTransform: 'uppercase', letterSpacing: '.03em', paddingBottom: 6 }}>
                  <span>Sale</span><span style={{ textAlign: 'right' }}>Đơn</span><span style={{ textAlign: 'right' }}>Công nợ</span>
                </div>
                {sales.map((s, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 26px 84px', alignItems: 'center', padding: '6px 0' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--bct-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.saleName || '(không rõ)'}</div>
                      {s.saleCode && <div className="mono" style={{ fontSize: 10, color: 'var(--bct-accent)' }}>{s.saleCode}</div>}
                    </div>
                    <span className="mono" style={{ fontSize: 12, textAlign: 'right', color: 'var(--bct-text3)' }}>{s.orders}</span>
                    <span className="mono" style={{ fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--bct-red)' }}>{num(s.debt)}</span>
                  </div>
                ))}
                {hidden > 0 && <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--bct-muted2)', marginTop: 8 }}>Đã ẩn {hidden} sale nợ &lt; 20tr</div>}
                {!sales.length && <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--bct-muted2)', padding: '4px 0' }}>Không có sale nợ ≥ 20tr</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Chi tiết công nợ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ width: 4, height: 15, borderRadius: 2, background: 'var(--bct-accent)' }} />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--bct-text)' }}>Chi tiết công nợ — ưu tiên nợ lâu / nợ lớn</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 16, marginBottom: 14 }}>
        {/* COD */}
        <div style={{ border: '1px solid var(--bct-border)', borderRadius: 13, background: 'var(--bct-surface)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--bct-line)' }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--bct-red-soft)', color: 'var(--bct-red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>$</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--bct-text)' }}>Công nợ COD chưa thu</div>
              <div style={{ fontSize: 11.5, color: 'var(--bct-muted)' }}>Hiện {codAll.length} KH nợ &gt; {COD_MIN_DAYS} ngày · sắp theo số nợ</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10.5, color: 'var(--bct-muted)' }}>Tổng công nợ</div>
              <div className="mono" style={{ fontSize: 15, fontWeight: 700, color: 'var(--bct-red)' }}>{money(detail?.total)}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 78px 156px', fontSize: 10, fontWeight: 600, color: 'var(--bct-muted2)', textTransform: 'uppercase', letterSpacing: '.03em', padding: '8px 16px', background: 'var(--bct-band)' }}>
            <span>Khách hàng</span><span>Sale</span><span style={{ textAlign: 'center' }}>Số ngày</span><span style={{ textAlign: 'right' }}>Công nợ</span>
          </div>
          {codRows.map((r, i) => {
            const dt = dayTone(r.maxDebtDays || 0);
            return (
              <div key={r._id} className="bct-row" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 78px 156px', alignItems: 'center', padding: '9px 16px', borderTop: i ? '1px solid var(--bct-line)' : 'none', cursor: 'pointer' }} onClick={() => onOpenCustomer?.(r._id, r.custName)}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--bct-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.custName || '—'}</div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--bct-accent)' }}>{r._id} · {r.orders} đơn</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--bct-text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.saleName || '—'}</div>
                <div style={{ textAlign: 'center' }}><span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: dt.c, background: dt.bg, padding: '2px 7px', borderRadius: 5 }}>{r.maxDebtDays} ngày</span></div>
                <div className="mono" style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: 'var(--bct-red)' }}>{money(r.debt)}</div>
              </div>
            );
          })}
          {!codRows.length && <div style={{ padding: 24, textAlign: 'center', color: 'var(--bct-muted2)', fontSize: 13 }}>Không có KH nợ &gt; {COD_MIN_DAYS} ngày.</div>}
          {codMore > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--bct-line)', fontSize: 12, color: 'var(--bct-muted)' }}>
              <span>+ {codMore} KH khác nợ &gt; {COD_MIN_DAYS} ngày · đã ẩn KH nợ nhỏ</span>
            </div>
          )}
        </div>

        {/* Cước nội địa & NVL */}
        <div style={{ border: '1px solid var(--bct-border)', borderRadius: 13, background: 'var(--bct-surface)', padding: '13px 16px', alignSelf: 'start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--bct-amber-soft)', color: 'var(--bct-amber)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🚚</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--bct-text)' }}>Cước nội địa &amp; NVL</div>
              <div style={{ fontSize: 11.5, color: 'var(--bct-muted)' }}>{shipCust.length} KH còn nợ</div>
            </div>
          </div>
          <div className="mono" style={{ fontSize: 23, fontWeight: 700, color: 'var(--bct-amber)' }}>{money(ship?.totalOwed)}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0 14px' }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: shipOver ? '#a06a1a' : '#0f7a55', background: shipOver ? 'var(--bct-amber-soft)' : 'var(--bct-accent-soft)', padding: '3px 9px', borderRadius: 6 }}>{shipOver ? `⚠ ${shipOver} KH nợ > ${COD_MIN_DAYS} ngày` : `✓ 0 KH nợ > ${COD_MIN_DAYS} ngày`}</span>
            <span style={{ fontSize: 11.5, color: 'var(--bct-muted)', background: 'var(--bct-chip)', padding: '3px 9px', borderRadius: 6 }}>{shipCust.length} KH còn nợ</span>
          </div>
          {shipTop.length > 0 && <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--bct-muted2)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 6 }}>Nợ cước lớn nhất</div>}
          {shipTop.map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', borderTop: i ? '1px solid var(--bct-line)' : 'none' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--bct-text)' }}>{s.custName || '—'} <span className="mono" style={{ fontSize: 10, fontWeight: 400, color: 'var(--bct-muted2)' }}>{s.custCode}</span></span>
              <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bct-amber)' }}>{money(s.totalOwed)}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--bct-muted2)' }}>
        Phát sinh tuần = Đầu kỳ + Doanh thu tuần · Đã thu = Phát sinh − Còn lại · Tỷ lệ = Đã thu ÷ Phát sinh · Còn lại = quét công nợ + cước/NVL.
      </div>
    </div>
  );
}
